// src\api\base.ts

import { BaseQueryFn, FetchArgs, FetchBaseQueryError, createApi } from '@reduxjs/toolkit/query/react';
import axios from 'axios';
import { Mutex } from 'async-mutex';
import { RootState } from 'store';
import { clearToken, updateMeInfo } from 'store/slice/auth-slice';
import { isTimestampExpired } from 'utils/date';
import { getBaseUrl } from 'utils/getAPI-config';

// Set base URL for the API dynamically depending on the environment.
// `getBaseUrl()` is used to retrieve the correct API base URL.
export const BASE_URL = getBaseUrl(); // Get the current BASE_URL

// `API_V1` is a constant used to standardize the base path of all API endpoints.
export const API_V1 = 'api/v1';

// Create a mutex (mutual exclusion) to prevent simultaneous API requests that might refresh the token.
const mutex = new Mutex();

/**
 * A base query function using Axios to make API requests. This query checks and dynamically sets the content type,
 * adds authorization if needed, and handles API errors.
 *
 * @param {object} baseURL - The base URL for the API (default is retrieved from `getBaseUrl()`).
 * @returns {Function} - A function that takes API arguments, state, and performs the request.
 */
const axiosBaseQuery =
  ({ baseURL } = { baseURL: getBaseUrl() }) =>
  async (args: any, api: any, _: any) => {
    // Dynamically set 'Content-Type' using 'cheaders', fallback to 'application/json' if not provided
    const contentTypeHeader = args.cheaders || args.headers?.['Content-Type'] || 'application/json';
    try {
      const loginWhitelist = [
        'api/v1/Login/getloginType',
        'api/v1/Login/Version',
        'api/v1/Login',
        'api/v1/Login/Logout',
        'api/v1/Login/ResetPassword',
        'api/v1/Login/Verify',
        'api/v1/Login/ForgotPassword'
      ];
      const allowApiCall = loginWhitelist.includes(args.url) || localStorage.getItem('isLoggedOut') !== 'true';

      if (!allowApiCall) {
        console.log('Logged out ... Unable to API call =>' + args.url);
        return { error: { status: 401, data: 'User is logged out' } };
      }
      const result = await axios({
        baseURL: getBaseUrl(),
        withCredentials: true,
        headers: {
          ...args.headers,
          'Content-Type': contentTypeHeader // Set 'Content-Type' dynamically based on 'cheaders'
        },
        ...args // Spread other request properties (url, method, params, etc.)
      });

      return { data: result.data, meta: result.headers };
    } catch (err: any) {
      return {
        error: {
          status: err.response?.status, // Return error status
          data: err.response?.data || err.message // Return error data or message
        }
      };
    }
  };

// Create a custom base query with the default base URL
export const customBaseQuery = axiosBaseQuery({
  baseURL: BASE_URL
});

/**
 * Handles token expiry and attempts to refresh the token when necessary.
 * This function is used to handle authentication and make sure the user's session is valid.
 *
 * @param {any} args - The API request arguments.
 * @param {any} api - The Redux API object, used to dispatch and get state.
 * @param {any} extraOptions - Additional options for the API call.
 * @returns {Promise<boolean>} - Returns `true` if the token was refreshed successfully, otherwise `false`.
 */
const handleTokenExpiry = async (args: any, api: any, extraOptions: any): Promise<boolean> => {
  // Check if the mutex is locked, which means a token refresh is in progress
  if (!mutex.isLocked()) {
    const release = await mutex.acquire(); // Acquire the mutex to ensure only one refresh request is made
    try {
      // Attempt to refresh the token by making a call to the refresh endpoint
      await customBaseQuery(
        {
          url: `${API_V1}/Login/Refresh`,
          method: 'PUT'
        },
        api,
        extraOptions
      );

      const userInfoResponse: any = await customBaseQuery(
        {
          url: `${API_V1}/User/me`,
          method: 'GET'
        },
        api,
        extraOptions
      );
      // If a new token is received, update it in the Redux store
      if (userInfoResponse.data?.nameid) {
        await api.dispatch(updateMeInfo({ decodedAuthToken: userInfoResponse.data }));
        return true;
      } else {
        api.dispatch(clearToken()); // Clear the token if refresh fails
        return false;
      }
    } finally {
      release(); // Release the mutex after refreshing or failing
    }
  } else {
    await mutex.waitForUnlock(); // If the mutex is locked, wait for it to be released
    return true;
  }
};

/**
 * This base query function adds reauthentication logic.
 * It checks if the token has expired, handles token refresh, and retries the API call if necessary.
 *
 * @param {any} args - The API request arguments.
 * @param {any} api - The Redux API object, used to dispatch and get state.
 * @param {any} extraOptions - Additional options for the API call.
 * @returns {Promise<any>} - The result of the API request.
 */
const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
  // Wait for any ongoing token refresh to complete before proceeding
  await mutex.waitForUnlock();

  // Check if the current token has expired
  const { decodedAuthToken } = (api.getState() as RootState).auth;
  const makeApiCall =
    decodedAuthToken && isTimestampExpired(decodedAuthToken?.exp) && args?.url !== 'api/v1/Login/Logout'
      ? await handleTokenExpiry(args, api, extraOptions) // If token expired, handle reauthentication
      : Promise.resolve(true); // Otherwise, proceed with the API call
  // args?.url !== 'api/v1/Login/Logout' To allow logout API for further execution even if refresh token is failed

  if (!makeApiCall) {
    return; // If token refresh failed, do not make the API call
  }

  let result: any = await customBaseQuery(args, api, extraOptions); // Make the API call with the current or refreshed token

  // If the API doesn't require authentication, return the result
  if (typeof args === 'object' && args.hasOwnProperty('noAuth')) {
    return result;
  }

  // Check for 401 errors (unauthorized) or specific error codes indicating token expiry
  if (
    (result.error && result.error.status === 401) ||
    (result.data && result.data['ErrorCode'] === 401 && (result.data['ErrorMessage'] ?? '').includes('Expired token'))
  ) {
    const _makeApiCall = await handleTokenExpiry(args, api, extraOptions); // Attempt to refresh the token
    if (!_makeApiCall) {
      return; // If token refresh failed again, return without retrying
    }
    result = await customBaseQuery(args, api, extraOptions); // Retry the original API request after refreshing the token
  }

  return result; // Return the final API response
};

/**
 * Create an API service using Redux Toolkit's `createApi`. This service includes automatic token management and retry logic.
 * The `baseQueryWithReauth` function ensures that API calls are properly authenticated and retried if needed.
 *
 * @returns {object} - The base API configuration.
 */
const baseApi = createApi({
  baseQuery: baseQueryWithReauth, // Use the base query with reauthentication logic
  endpoints: () => ({}), // Define endpoints later in other files
  keepUnusedDataFor: 0 // Clear unused data from cache immediately
});

export default baseApi;
